import { Directive, ElementRef, OnInit, Optional } from '@angular/core';
import { Tooltip } from 'primeng/tooltip';

/**
 * Re-anchors a PrimeNG tooltip arrow onto its trigger icon.
 *
 * PrimeNG pins the arrow to the centre (50%) of the tooltip box (via a runtime
 * injected `top: 50%` / `margin-top` rule, not `!important`). That points at the
 * icon only while the box stays where PrimeNG first placed it. A wide hint is
 * measured, then clamped back inside the viewport (or nudged by CSS), and once the
 * box moves the fixed-centre arrow drifts off the icon — visible on iPad mini.
 *
 * Timing this from the outside (a fixed rAF, an observer, a poll) is unreliable:
 * PrimeNG builds the tooltip in async steps and may add a show delay, so a pass
 * can land before the arrow child exists — the "one in a few" flicker. Instead we
 * hook PrimeNG's own `align()`. PrimeNG calls it every time it positions the box
 * (show, scroll, resize), and by then the container and arrow are built and
 * placed, so anchoring right after it is deterministic. A trailing rAF also covers
 * the same-show box clamp that nudges the box one frame later. Only the arrow is
 * touched; the box is never moved.
 */
@Directive({
  selector: '[tooltipArrowAnchor]'
})
export class TooltipArrowAnchorDirective implements OnInit {
  // Keep the arrow off the box's rounded corners.
  private readonly edgePadding = 10;

  private patched = false;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    @Optional() private readonly tooltip?: Tooltip,
  ) {}

  ngOnInit(): void {
    // Wrap before any show can run, so no first-show slips through unwrapped
    // (PrimeNG's own show/align listener may fire before an event-based hook).
    this.patchAlign();
  }

  /**
   * Wrap the PrimeNG Tooltip instance's `align()` so our anchoring runs right after
   * every reposition. Done once per instance; the wrapper shadows the prototype
   * method so PrimeNG's own `this.align()` call flows through it.
   */
  private patchAlign(): void {
    // `align` is an internal method, not on the public type, so reach it untyped.
    const tooltip = this.tooltip as unknown as { align?: () => void } | undefined;
    if (this.patched || !tooltip || typeof tooltip.align !== 'function') {
      return;
    }
    this.patched = true;
    const originalAlign = tooltip.align.bind(tooltip);
    tooltip.align = (): void => {
      originalAlign();
      this.anchorArrow();
      // The right-expand box clamp moves the box a frame after align; re-anchor then.
      requestAnimationFrame(() => this.anchorArrow());
    };
  }

  private anchorArrow(): void {
    const container = this.tooltip?.container as HTMLElement | undefined;
    if (!container) {
      return;
    }
    const arrow = container.querySelector('.p-tooltip-arrow') as HTMLElement | null;
    if (!arrow) {
      return;
    }

    const box = container.getBoundingClientRect();
    const icon = this.host.nativeElement.getBoundingClientRect();

    // Left/right tooltips carry a vertical arrow (moves along top); top/bottom
    // tooltips carry a horizontal one (moves along left). Detect from the class
    // PrimeNG applied so a flipped tooltip is still handled.
    const isVerticalArrow =
      container.classList.contains('p-tooltip-left') || container.classList.contains('p-tooltip-right');

    if (isVerticalArrow) {
      const iconCenterY = icon.top + icon.height / 2;
      const offset = this.clampToEdge(iconCenterY - box.top - arrow.offsetHeight / 2, box.height, arrow.offsetHeight);
      arrow.style.top = `${offset}px`;
      arrow.style.bottom = 'auto';
      arrow.style.marginTop = '0';
    } else {
      const iconCenterX = icon.left + icon.width / 2;
      const offset = this.clampToEdge(iconCenterX - box.left - arrow.offsetWidth / 2, box.width, arrow.offsetWidth);
      arrow.style.left = `${offset}px`;
      arrow.style.right = 'auto';
      arrow.style.marginLeft = '0';
    }
  }

  /** Keep the arrow on the straight part of the box edge, off the corners. */
  private clampToEdge(offset: number, boxSize: number, arrowSize: number): number {
    return Math.min(Math.max(offset, this.edgePadding), boxSize - arrowSize - this.edgePadding);
  }
}
